import * as Model from "@effect/sql/Model";
import { Schema } from "effect";

/**
 * What kind of credential a row names.
 *
 * - `claude_oauth`: a Claude Code OAuth token (`CLAUDE_CODE_OAUTH_TOKEN`)
 * - `codex_oauth`: the verbatim `~/.codex/auth.json` produced by `codex login`
 * - `anthropic_api_key` / `openai_api_key`: plain API keys
 * - `github_token`: the person's own GitHub token, so the agent pushes and
 *   comments AS them rather than as a shared app installation
 * - `linear_api_key`: the person's Linear key, same reasoning
 * - `aws_access_key`: needs more than the two fixed token slots, so it lives in
 *   the encrypted `secretFields` map (accessKeyId / secretAccessKey / region)
 *
 * The OAuth kinds are PREFERRED over the API-key kinds at lease time — an
 * OAuth seat is what the worker image's entrypoint actually knows how to use.
 *
 * Stored as plain varchar, never a pgEnum — adding a kind must not require an
 * `ALTER TYPE` and the two-deploy enum-value-isolation dance.
 *
 * The wire protocol declares its OWN identical literal in
 * `@fm/api`'s `rpc/orca-runner.schemas.ts` (that module imports `effect` and
 * nothing else, by design, so it cannot import this one). A unit test asserts
 * the two sets are equal so they cannot drift.
 *
 * @since 1.0.0
 */
export const CredentialKind = Schema.Literal(
  "claude_oauth",
  "codex_oauth",
  "anthropic_api_key",
  "openai_api_key",
  "github_token",
  "linear_api_key",
  "aws_access_key",
);
export type CredentialKind = Schema.Schema.Type<typeof CredentialKind>;

/**
 * Whether a credential may be leased at all.
 *
 * - `active`: leasable
 * - `disabled`: the operator's off-switch, never revived by the lease's lazy
 *   exhaustion expiry
 * - `revoked`: the OWNER withdrew consent. Terminal — the secret columns are
 *   meant to be cleared alongside it, and nothing ever flips it back.
 *
 * Stored as plain varchar, never a pgEnum — see {@link CredentialKind}.
 *
 * @since 1.0.0
 */
export const CredentialStatus = Schema.Literal("active", "disabled", "revoked");
export type CredentialStatus = Schema.Schema.Type<typeof CredentialStatus>;

/**
 * Credential model — one PERSON's own credential, encrypted at rest (OL-63).
 *
 * A credential is a human's identity, so `personId` is the owner and every
 * accessor filters on it first. `customerId` is the scope that person belongs
 * to — placement, billing and admin listing — and is never the owner: two
 * members of one customer may each hold a credential labelled `github`.
 *
 * `accessToken`, `refreshToken` and `secretFields` are {@link Model.Sensitive},
 * which drops them from the `json` / `jsonCreate` / `jsonUpdate` variants
 * entirely. That is the MECHANICAL guard behind "admins can see that a
 * credential exists, they can never read it": a future handler that serves
 * `Credential.json` cannot leak a secret column by accident, because the key is
 * not in the schema to serve.
 *
 * Their values are AES-256-GCM ciphertext even on the `select` variant — the
 * accessor encrypts on write and deliberately does NOT decrypt on read, so the
 * runner-RPC redeem stays the single decrypt point.
 *
 * `customerId` and `personId` are plain strings with no foreign key:
 * `customers` / `people` live in the `public` schema while this table lives in
 * `orcaloops`, and the reference is by id only so the schema can be split out.
 *
 * @since 1.0.0
 */
export class Credential extends Model.Class<Credential>("Credential")({
  id: Model.Generated(Schema.String),

  customerId: Schema.String,

  kind: CredentialKind,
  label: Schema.String,
  status: CredentialStatus,

  /** Epoch until which this credential is treated as unusable. */
  exhaustedUntil: Schema.NullOr(Schema.DateTimeUtc),
  /** When it was last leased — the lease's LRU key. */
  lastUsedAt: Schema.NullOr(Schema.DateTimeUtc),

  createdAt: Model.DateTimeInsertFromDate,
  updatedAt: Model.DateTimeUpdateFromDate,

  /** The OWNING person. Authorizes every read, lease and revocation. */
  personId: Schema.String,

  /** AES-256-GCM ciphertext. Absent from every JSON variant (Sensitive). */
  accessToken: Model.Sensitive(Schema.NullOr(Schema.String)),
  /** AES-256-GCM ciphertext. Absent from every JSON variant (Sensitive). */
  refreshToken: Model.Sensitive(Schema.NullOr(Schema.String)),
  /**
   * AES-256-GCM ciphertext over a `JSON.stringify`d field-name→value map, for
   * kinds needing more than the two fixed slots. Absent from every JSON
   * variant (Sensitive).
   */
  secretFields: Model.Sensitive(Schema.NullOr(Schema.String)),

  /** When the provider says the access token stops working. */
  tokenExpiresAt: Schema.NullOr(Schema.DateTimeUtc),
  /** Scopes the person granted, as the provider reported them. */
  scopes: Schema.NullOr(Schema.Array(Schema.String)),
  /** When a LEASE was last minted against this row. */
  lastLeasedAt: Schema.NullOr(Schema.DateTimeUtc),
}) {}
